import { useState } from 'react'
import { useTranslate } from '@ur-solutions/react-hooks'
import { FAIcon } from '@/components'
import { cn } from '@/lib/utils'

export interface UserWorkPeriodsWarningEntry {
  user: { id: string; fullName: string }
  numTasks: number
}

/**
 * Collapsible amber notice listing users who each have some number of work
 * periods needing attention (missing approval, changed after lock, ...). The
 * caller provides the resolved header `title`; clicking a user invokes
 * `onSelectUser` (typically to filter a view to that user).
 */
export const UserWorkPeriodsWarningCard: React.FC<{
  users: UserWorkPeriodsWarningEntry[]
  title: string
  onSelectUser: (userId: string) => void
}> = ({ users, title, onSelectUser }) => {
  const [open, setOpen] = useState(false)
  const translations = useTranslate({
    nWorkPeriods: ['salary.n-work-periods', { n: 0 }],
  })

  if (users.length === 0) return null

  return (
    <div
      role="button"
      tabIndex={0}
      className="cursor-pointer rounded-xl border border-amber-300 bg-amber-50 p-4 shadow-sm"
      onClick={() => setOpen(value => !value)}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          setOpen(value => !value)
        }
      }}
    >
      <div className="flex items-center gap-2 text-amber-800">
        <FAIcon icon="exclamation-triangle" />
        <p className="font-semibold">{title}</p>
        <FAIcon
          icon="chevron-down"
          className={cn(
            'ml-auto text-xs transition-transform',
            open && 'rotate-180'
          )}
        />
      </div>

      {open && (
        <div className="mt-3 flex flex-col gap-1.5">
          {users.map(({ user, numTasks }) => (
            <button
              key={user.id}
              type="button"
              className="text-left text-sm font-medium text-primary underline-offset-2 hover:underline"
              onClick={event => {
                event.stopPropagation()
                onSelectUser(user.id)
                setOpen(false)
              }}
            >
              {user.fullName} ({translations.nWorkPeriods({ n: numTasks })})
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
